import { BaseService } from "./base.service";
export type { Member } from "../types/member";
import type { Member, UpdateMemberContactRequest } from "../types/member";
import api from "./api";

class MembersService extends BaseService {
  constructor() {
    super("/members");
  }

  async getMembers(dto: any): Promise<{ data: Member[]; total: number }> {
    return this.post<{ data: Member[]; total: number }>("/getAll", dto);
  }

  async getMember(id: string): Promise<Member> {
    return this.get<Member>(`/${id}`);
  }

  async getManagedMembers(): Promise<Member[]> {
    return this.get<Member[]>("/managed-members");
  }

  async getManagedMembersForMember(memberId: string): Promise<Member[]> {
    return this.get<Member[]>(`/${memberId}/managed-members`);
  }

  async getMembersWhoCanAccess(memberId: string): Promise<Member[]> {
    return this.get<Member[]>(`/${memberId}/accessible-by`);
  }
  async revokeInvite(id: string): Promise<any> {
    return this.delete(`/${id}/member-invite`);
  }
  async inviteMember(id: string, mode?: "email" | "mobile"): Promise<any> {
    return this.post<any>(`/${id}/member-invite`, mode ? { mode } : {});
  }

  /**
   * Adds a new member and associates with a family via /members/add-member
   */
  /**
   * Adds a new member and associates with a family via /members/add-member or /add-member-by-fm
   * Converts camelCase payload keys to snake_case to match backend DTO/schema.
   * Uses different endpoint based on user type (FO vs FM).
   */
  async createMember(data: Partial<Member>): Promise<any> {
    // Get current user info to determine endpoint
    // Use 'member' key which is the standard across the codebase
    const memberStr = localStorage.getItem('member');
    const currentUser = memberStr ? JSON.parse(memberStr) : {};
    
    // Check multiple possible sources for family_member_type
    const familyMemberType = currentUser.family_member_type || 
                            currentUser.memberType || 
                            currentUser.derivedMemberType;
    
    const isFamilyOwner = familyMemberType === 'fo';
    
    // Utility to convert camelCase to snake_case
    const toSnakeCase = (str: string) =>
      str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    const transformKeys = (obj: Record<string, any>) => {
      const result: Record<string, any> = {};
      Object.entries(obj).forEach(([key, value]) => {
        const newKey = toSnakeCase(key);
        result[newKey] = value;
      });
      return result;
    };

    // Convert empty string values for nullable fields to null
    const transformed = transformKeys(data);
    ["dob_month", "dob_year"].forEach((field) => {
      if (
        field in transformed &&
        (transformed[field] === "" || transformed[field] === undefined)
      ) {
        transformed[field] = null;
      }
    });
    
    // Use appropriate endpoint based on user type
    const endpoint = isFamilyOwner ? "/add-member" : "/add-member-by-fm";
    return this.post<any>(endpoint, transformed);
  }

  async updateMember(
    idOrMember: string | Partial<Member>,
    data?: Partial<Member>
  ): Promise<Member> {
    // Accept either id string or member object
    let id: string | undefined;
    let payload: Partial<Member>;
    if (typeof idOrMember === "string") {
      id = idOrMember;
      payload = data || {};
    } else {
      id =
        (idOrMember as any)._id ||
        (idOrMember as any).id ||
        (data && ((data as any)._id || (data as any).id));
      payload = { ...(data || idOrMember) };
      // Remove _id/id from payload to avoid accidental update
      delete (payload as any)._id;
      delete (payload as any).id;
    }
    if (!id) {
      // Provide more context for debugging
      throw new Error(
        `No member id provided for update. Received: ${JSON.stringify(
          idOrMember
        )}, ${JSON.stringify(data)}`
      );
    }
    return this.put<Member>(`/${id}`, payload);
  }

  async updateMemberContact(
    id: string,
    data: UpdateMemberContactRequest
  ): Promise<Member> {
    return this.put<Member>(`/${id}/contact`, data);
  }

  async deleteMember(id: string): Promise<void> {
    return this.delete(`/${id}`);
  }

  /**
   * Update last access date for the logged-in member
   */
  async updateLastAccessDate(): Promise<Member> {
    return this.post<Member>('/last-access', {});
  }
}

// DTO for exit family action
export interface ExitFamilyDto {
  action: "exit" | "delete";
  newOwnerId?: string;
}

// Service function for FO exit with ownership transfer
export async function exitFamilyAsOwner(dto: ExitFamilyDto) {
  return api.delete("/members/owner/profile", { data: dto });
}

// Service function for Member exit
export async function exitFamilyAsMember(dto: ExitFamilyDto) {
  return api.delete("/members/member/profile", { data: dto });
}

export const membersService = new MembersService();
